import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { iniciarAutorizacao, type AcaoNoKeycloak } from '@/nucleo/auth/fluxo-autorizacao';

/**
 * O segundo fator da pessoa conectada, para o cartão "Método do segundo fator"
 * de "Meu perfil".
 *
 * LEITURA pela API (`GET /eu/segundo-fator`, que lê as credenciais da conta no
 * Keycloak). A TROCA não passa pela API: o navegador vai ao Keycloak com a ação
 * pedida, o Keycloak confere o método novo (um código do aparelho novo, ou um
 * código que chegou ao e-mail) e só então grava. Não existe rota nossa que
 * grave segundo fator — nada aqui finge gravar.
 */

export type MetodoSegundoFator = 'aplicativo' | 'email';

/** Contrato de `GET /eu/segundo-fator` (apps/api/src/modulos/eu/segundo-fator.ts). */
export interface SegundoFatorDoUsuario {
  metodo: MetodoSegundoFator | null;
  destinoMascarado: string | null;
  codigoPorEmailDisponivel: boolean;
}

export const CHAVE_SEGUNDO_FATOR = ['eu', 'segundo-fator'] as const;

/** 401 e 403 não se resolvem tentando de novo (ver admin/integracoes/dados.ts). */
function naoInsistirEmRecusa(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroApi && (erro.pedeEntrarDeNovo || erro.pedeOutroCaminho)) {
    return false;
  }
  return tentativas < 1;
}

export function useSegundoFator(): UseQueryResult<SegundoFatorDoUsuario, ErroApi> {
  return useQuery<SegundoFatorDoUsuario, ErroApi>({
    queryKey: CHAVE_SEGUNDO_FATOR,
    queryFn: ({ signal }) =>
      chamarApi<SegundoFatorDoUsuario>('/eu/segundo-fator', { sinal: signal }),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * "Editar meus dados": nome e sobrenome da própria pessoa (`PATCH /eu/dados`).
 * A API grava no Keycloak e no espelho do sistema, com registro na trilha, e
 * devolve o nome de exibição novo. Não há identificador: a conta é a do token.
 */
export function salvarMeusDados(dados: {
  nome: string;
  sobrenome: string;
}): Promise<{ nome: string }> {
  return chamarApi<{ nome: string }>('/eu/dados', { metodo: 'PATCH', corpo: dados });
}

/** A ação do Keycloak que cadastra cada método. */
export const ACAO_DO_METODO: Record<MetodoSegundoFator, AcaoNoKeycloak> = {
  aplicativo: 'CONFIGURE_TOTP',
  email: 'teg-cadastrar-codigo-email',
};

/**
 * Leva a pessoa ao Keycloak para trocar de método. Na volta, ela cai de novo
 * nesta mesma tela, que lê o método da API outra vez — o que a tela mostra
 * depois é o que o Keycloak gravou, ou o de antes, se a troca foi cancelada.
 */
export function trocarSegundoFator(para: MetodoSegundoFator, destino: string): Promise<void> {
  return iniciarAutorizacao({ destino, acaoNoKeycloak: ACAO_DO_METODO[para] });
}
